import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye,
  Bike,
  FileText,
  AlertTriangle,
  Loader,
  User,
  Calendar,
  Mail,
  MoreVertical
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDateDisplay } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';

const Approvals = ({ user }) => {
  const { isMobile, isTablet } = useDeviceDetection();
  const [loading, setLoading] = useState(true);
  const [pendingData, setPendingData] = useState({
    motorcycles: [],
    handovers: [],
    takeovers: [],
    accidents: [],
    riders: [],
    lessees: [],
    total_count: 0
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeTab, setActiveTab] = useState('motorcycles');

  // Check if user has permission for a specific approval sub-page
  const hasSubPagePermission = (subPage) => {
    // Super Admin and Admin have all permissions
    if (user?.role === 'Super Admin' || user?.role === 'Admin') {
      return true;
    }
    // Check specific sub-page permission
    const approvalPerms = user?.permissions?.approvals;
    if (approvalPerms?.subPages?.[subPage]?.view) {
      return true;
    }
    // Also check if main approvals view is enabled (legacy support)
    if (approvalPerms?.view) {
      return true;
    }
    return false;
  };

  // Define all approval tabs with their permissions
  const allTabs = [
    { key: 'motorcycles', label: 'Motorcycles', permission: 'motorcycles', color: 'purple', icon: Bike },
    { key: 'handovers', label: 'Handovers', permission: 'handovers', color: 'blue', icon: FileText },
    { key: 'takeovers', label: 'Takeovers', permission: 'takeovers', color: 'green', icon: FileText },
    { key: 'accidents', label: 'Accidents', permission: 'accidents', color: 'red', icon: AlertTriangle },
    { key: 'riders', label: 'Riders', permission: 'riders', color: 'teal', icon: User },
    { key: 'lessees', label: 'Lessees', permission: 'lessees', color: 'indigo', icon: User },
  ];

  // Filter tabs based on user permissions
  const visibleTabs = allTabs.filter(tab => {
    // Motorcycles tab is always visible if user can access approvals page
    if (tab.key === 'motorcycles') {
      return user?.role === 'Super Admin' || user?.role === 'Admin' || user?.permissions?.approvals?.view;
    }
    return hasSubPagePermission(tab.permission);
  });

  // Set initial active tab to first visible tab
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find(t => t.key === activeTab)) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/approvals/pending`, {
        headers: getAuthHeader()
      });
      setPendingData(response.data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view approvals');
      } else {
        toast.error('Failed to load pending approvals');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (item, type) => {
    setSelectedItem({ ...item, type });
    setRejectionReason('');
    setReviewDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedItem) return;
    
    setProcessing(true);
    try {
      const collection = selectedItem.type === 'motorcycle' ? 'motorcycles' : 
                        selectedItem.type === 'handover' ? 'motorcycle_handovers' : 
                        selectedItem.type === 'takeover' ? 'motorcycle_takeovers' : 
                        selectedItem.type === 'accident' ? 'accident_reports' :
                        selectedItem.type === 'rider' ? 'riders' :
                        'lessees';
      
      await axios.post(
        `${API}/approvals/${collection}/${selectedItem.id}/approve`,
        {},
        { headers: getAuthHeader() }
      );

      toast.success('Entry approved successfully!');
      setReviewDialogOpen(false);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error approving entry:', error);
      toast.error('Failed to approve entry');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedItem) return;
    
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      const collection = selectedItem.type === 'motorcycle' ? 'motorcycles' : 
                        selectedItem.type === 'handover' ? 'motorcycle_handovers' : 
                        selectedItem.type === 'takeover' ? 'motorcycle_takeovers' : 
                        selectedItem.type === 'accident' ? 'accident_reports' :
                        selectedItem.type === 'rider' ? 'riders' :
                        'lessees';
      
      await axios.post(
        `${API}/approvals/${collection}/${selectedItem.id}/reject`,
        { reason: rejectionReason },
        { headers: getAuthHeader() }
      );

      toast.success('Entry rejected');
      setReviewDialogOpen(false);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error rejecting entry:', error);
      toast.error('Failed to reject entry');
    } finally {
      setProcessing(false);
    }
  };

  const MotorcycleCard = ({ motorcycle }) => (
    <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-orange-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <Bike className="w-8 h-8 text-orange-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-lg">{motorcycle.plate_number}</h3>
                <Badge className="bg-purple-100 text-purple-800 font-mono">
                  {motorcycle.internal_id}
                </Badge>
                <Badge className="bg-orange-100 text-orange-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                <div><span className="font-medium">Chassis:</span> {motorcycle.chassis_number}</div>
                <div><span className="font-medium">Model:</span> {motorcycle.model}</div>
                <div><span className="font-medium">Color:</span> {motorcycle.color}</div>
                <div><span className="font-medium">Year:</span> {motorcycle.year}</div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>Created by: {motorcycle.created_by}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDateDisplay(motorcycle.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => handleReview(motorcycle, 'motorcycle')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const HandoverCard = ({ handover }) => (
    <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-lg">Handover #{handover.id?.substring(0, 8)}</h3>
                <Badge className="bg-orange-100 text-orange-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                <div><span className="font-medium">Bike:</span> {handover.bike_plate || 'N/A'}</div>
                <div><span className="font-medium">Rider:</span> {handover.rider_name || 'N/A'}</div>
                <div><span className="font-medium">Lessee:</span> {handover.lessee_name || 'N/A'}</div>
                <div><span className="font-medium">Date:</span> {formatDateDisplay(handover.handover_date)}</div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>Created by: {handover.created_by}</span>
                </div>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => handleReview(handover, 'handover')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const AccidentCard = ({ accident }) => (
    <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-lg">Accident Report #{accident.id?.substring(0, 8)}</h3>
                <Badge className="bg-orange-100 text-orange-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                <div><span className="font-medium">Bike:</span> {accident.bike_plate || 'N/A'}</div>
                <div><span className="font-medium">Rider:</span> {accident.rider_name || 'N/A'}</div>
                <div><span className="font-medium">Date:</span> {formatDateDisplay(accident.accident_date)}</div>
                <div><span className="font-medium">Location:</span> {accident.location || 'N/A'}</div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>Created by: {accident.created_by}</span>
                </div>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => handleReview(accident, 'accident')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const TakeoverCard = ({ takeover }) => (
    <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <FileText className="w-8 h-8 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-lg">Takeover #{takeover.id?.substring(0, 8)}</h3>
                <Badge className="bg-orange-100 text-orange-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                <div><span className="font-medium">Bike:</span> {takeover.bike_plate_number || 'N/A'}</div>
                <div><span className="font-medium">Rider:</span> {takeover.rider_name || 'N/A'}</div>
                <div><span className="font-medium">Type:</span> {takeover.takeover_type || 'N/A'}</div>
                <div><span className="font-medium">Date:</span> {formatDateDisplay(takeover.takeover_date)}</div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>Created by: {takeover.created_by}</span>
                </div>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => handleReview(takeover, 'takeover')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const RiderCard = ({ rider }) => (
    <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-teal-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <User className="w-8 h-8 text-teal-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-lg">{rider.name}</h3>
                <Badge className="bg-orange-100 text-orange-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                <div><span className="font-medium">Email:</span> {rider.email || 'N/A'}</div>
                <div><span className="font-medium">Phone:</span> {rider.mobile_uae || rider.phone_number || 'N/A'}</div>
                <div><span className="font-medium">Nationality:</span> {rider.nationality || 'N/A'}</div>
                <div><span className="font-medium">License:</span> {rider.license_number || 'N/A'}</div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>Created by: {rider.created_by || 'Bulk Upload'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDateDisplay(rider.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => handleReview(rider, 'rider')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const LesseeCard = ({ lessee }) => (
    <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-indigo-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <User className="w-8 h-8 text-indigo-600 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-lg">{lessee.company_name || lessee.name}</h3>
                <Badge className="bg-orange-100 text-orange-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                <div><span className="font-medium">Contact:</span> {lessee.contact_person || 'N/A'}</div>
                <div><span className="font-medium">Phone:</span> {lessee.phone_number || 'N/A'}</div>
                <div><span className="font-medium">Email:</span> {lessee.email || 'N/A'}</div>
                <div><span className="font-medium">TRN:</span> {lessee.trn_number || 'N/A'}</div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>Created by: {lessee.created_by}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>{formatDateDisplay(lessee.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          <Button
            size="sm"
            onClick={() => handleReview(lessee, 'lessee')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-slate-800">Pending Approvals</h1>
        <p className="text-slate-600 mt-2">Review and approve data entries</p>
      </div>

      {/* Statistics Cards - Only show for visible tabs */}
      <div className={`grid grid-cols-1 md:grid-cols-${Math.min(visibleTabs.length + 1, 7)} gap-4`}>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Pending</p>
                <p className="text-3xl font-bold text-orange-600">
                  {visibleTabs.reduce((sum, tab) => sum + (pendingData[tab.key]?.length || 0), 0)}
                </p>
              </div>
              <Clock className="w-10 h-10 text-orange-600 opacity-20" />
            </div>
          </CardContent>
        </Card>

        {visibleTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <Card key={tab.key}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">{tab.label}</p>
                    <p className={`text-3xl font-bold text-${tab.color}-600`}>{pendingData[tab.key]?.length || 0}</p>
                  </div>
                  <Icon className={`w-10 h-10 text-${tab.color}-600 opacity-20`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs for Different Types - Only show tabs user has permission for */}
      {loading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Loader className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">Loading pending approvals...</p>
          </CardContent>
        </Card>
      ) : visibleTabs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">You don't have permission to view any approval types</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full grid-cols-${visibleTabs.length}`}>
            {visibleTabs.map(tab => (
              <TabsTrigger 
                key={tab.key}
                value={tab.key} 
                className={`data-[state=active]:bg-${tab.color}-600 data-[state=active]:text-white`}
              >
                {tab.label} ({pendingData[tab.key]?.length || 0})
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="motorcycles" className="space-y-4 mt-4">
            {pendingData.motorcycles?.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Bike className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No pending motorcycle approvals</p>
                </CardContent>
              </Card>
            ) : (
              pendingData.motorcycles?.map(motorcycle => (
                <MotorcycleCard key={motorcycle.id} motorcycle={motorcycle} />
              ))
            )}
          </TabsContent>

          <TabsContent value="handovers" className="space-y-4 mt-4">
            {pendingData.handovers?.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No pending handover approvals</p>
                </CardContent>
              </Card>
            ) : (
              pendingData.handovers?.map(handover => (
                <HandoverCard key={handover.id} handover={handover} />
              ))
            )}
          </TabsContent>

          <TabsContent value="takeovers" className="space-y-4 mt-4">
            {pendingData.takeovers?.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No pending takeover approvals</p>
                </CardContent>
              </Card>
            ) : (
              pendingData.takeovers?.map(takeover => (
                <TakeoverCard key={takeover.id} takeover={takeover} />
              ))
            )}
          </TabsContent>

          <TabsContent value="accidents" className="space-y-4 mt-4">
            {pendingData.accidents?.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No pending accident approvals</p>
                </CardContent>
              </Card>
            ) : (
              pendingData.accidents?.map(accident => (
                <AccidentCard key={accident.id} accident={accident} />
              ))
            )}
          </TabsContent>

          <TabsContent value="riders" className="space-y-4 mt-4">
            {pendingData.riders?.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No pending rider approvals</p>
                </CardContent>
              </Card>
            ) : (
              pendingData.riders?.map(rider => (
                <RiderCard key={rider.id} rider={rider} />
              ))
            )}
          </TabsContent>

          <TabsContent value="lessees" className="space-y-4 mt-4">
            {pendingData.lessees?.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No pending lessee approvals</p>
                </CardContent>
              </Card>
            ) : (
              pendingData.lessees?.map(lessee => (
                <LesseeCard key={lessee.id} lessee={lessee} />
              ))
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Review Entry</DialogTitle>
            <DialogDescription>
              Review the details and approve or reject this entry
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <div className="space-y-4">
              {/* Entry Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Entry Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedItem.type === 'motorcycle' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Internal ID</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.internal_id}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Plate Number</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.plate_number}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Chassis Number</label>
                          <p className="text-base text-gray-900">{selectedItem.chassis_number}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Model</label>
                          <p className="text-base text-gray-900">{selectedItem.model_name || selectedItem.model}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Model Version</label>
                          <p className="text-base text-gray-900">{selectedItem.model_version || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Color</label>
                          <p className="text-base text-gray-900">{selectedItem.color}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Year of Manufacturing</label>
                          <p className="text-base text-gray-900">{selectedItem.year_of_manufacturing || selectedItem.year}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Status</label>
                          <Badge className="bg-green-100 text-green-800">{selectedItem.status}</Badge>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Lease Status</label>
                          <Badge className="bg-blue-100 text-blue-800">{selectedItem.lease_status || 'N/A'}</Badge>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Battery Level</label>
                          <p className="text-base text-gray-900">{selectedItem.current_battery_level || 0}%</p>
                        </div>
                      </div>
                      
                      {(selectedItem.lessee_name || selectedItem.lessee_id) && (
                        <div className="pt-4 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3">Lease Information</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-600">Lessee Name</label>
                              <p className="text-base text-gray-900">{selectedItem.lessee_name || 'Not assigned'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Lessee Contact</label>
                              <p className="text-base text-gray-900">{selectedItem.lessee_contact || 'N/A'}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Monthly Lease Amount</label>
                              <p className="text-base text-gray-900"><CurrencyDisplay amount={selectedItem.monthly_lease_amount || 0} /></p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Lease Start Date</label>
                              <p className="text-base text-gray-900">{selectedItem.lease_start_date ? formatDateDisplay(selectedItem.lease_start_date) : 'Not set'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Motorcycle Registration Photos */}
                      {selectedItem.registration_documents && selectedItem.registration_documents.length > 0 && (
                        <div className="pt-4 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3">Registration Documents</h4>
                          <div className="grid grid-cols-3 gap-3">
                            {selectedItem.registration_documents.map((doc, index) => {
                              const imageUrl = doc.startsWith('http') ? doc : `${API}${doc}`;
                              return (
                                <a 
                                  key={index} 
                                  href={imageUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img 
                                    src={imageUrl} 
                                    alt={`Registration document ${index + 1}`}
                                    className="w-full h-32 object-cover rounded border hover:opacity-75 transition"
                                  />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Submission Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created By</label>
                            <p className="text-base text-gray-900">{selectedItem.created_by}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created At</label>
                            <p className="text-base text-gray-900">{selectedItem.created_at ? formatDateDisplay(selectedItem.created_at) : 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedItem.type === 'handover' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Bike Plate</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.bike_plate_number}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Rider Name</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.rider_name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Lessee Name</label>
                          <p className="text-base text-gray-900">{selectedItem.lessee_name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Handover Date</label>
                          <p className="text-base text-gray-900">{formatDateDisplay(selectedItem.handover_date)}</p>
                        </div>
                        <div className="col-span-2">
                          <label className="text-sm font-medium text-gray-600">Notes</label>
                          <p className="text-base text-gray-900">{selectedItem.notes || 'No notes provided'}</p>
                        </div>
                      </div>
                      
                      {/* Handover Photos */}
                      {selectedItem.handover_photos && selectedItem.handover_photos.length > 0 && (
                        <div className="pt-4 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3">Handover Photos</h4>
                          <div className="grid grid-cols-3 gap-3">
                            {selectedItem.handover_photos.map((photo, index) => {
                              const imageUrl = photo.startsWith('http') ? photo : `${API}${photo}`;
                              return (
                                <a 
                                  key={index} 
                                  href={imageUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img 
                                    src={imageUrl} 
                                    alt={`Handover photo ${index + 1}`}
                                    className="w-full h-32 object-cover rounded border hover:opacity-75 transition"
                                  />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Submission Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created By</label>
                            <p className="text-base text-gray-900">{selectedItem.created_by}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedItem.type === 'takeover' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Bike Plate</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.bike_plate_number}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Rider Name</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.rider_name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Takeover Type</label>
                          <Badge className="bg-blue-100 text-blue-800">{selectedItem.takeover_type}</Badge>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Takeover Date</label>
                          <p className="text-base text-gray-900">{formatDateDisplay(selectedItem.takeover_date)}</p>
                        </div>
                        {selectedItem.takeover_type === 'temporary' && (
                          <>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Start Date</label>
                              <p className="text-base text-gray-900">{formatDateDisplay(selectedItem.temporary_start_date)}</p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">End Date</label>
                              <p className="text-base text-gray-900">{formatDateDisplay(selectedItem.temporary_end_date)}</p>
                            </div>
                            <div className="col-span-2">
                              <label className="text-sm font-medium text-gray-600">Reason</label>
                              <p className="text-base text-gray-900">{selectedItem.temporary_reason || 'No reason provided'}</p>
                            </div>
                          </>
                        )}
                        <div className="col-span-2">
                          <label className="text-sm font-medium text-gray-600">Notes</label>
                          <p className="text-base text-gray-900">{selectedItem.notes || 'No notes provided'}</p>
                        </div>
                      </div>
                      
                      {/* Takeover Photos */}
                      {selectedItem.takeover_photos && selectedItem.takeover_photos.length > 0 && (
                        <div className="pt-4 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3">Takeover Photos</h4>
                          <div className="grid grid-cols-3 gap-3">
                            {selectedItem.takeover_photos.map((photo, index) => {
                              const imageUrl = photo.startsWith('http') ? photo : `${API}${photo}`;
                              return (
                                <a 
                                  key={index} 
                                  href={imageUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img 
                                    src={imageUrl} 
                                    alt={`Takeover photo ${index + 1}`}
                                    className="w-full h-32 object-cover rounded border hover:opacity-75 transition"
                                  />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Submission Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created By</label>
                            <p className="text-base text-gray-900">{selectedItem.created_by}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedItem.type === 'accident' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Bike Plate</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.bike_plate}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Rider Name</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.rider_name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Accident Date</label>
                          <p className="text-base text-gray-900">{formatDateDisplay(selectedItem.accident_date)}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Location</label>
                          <p className="text-base text-gray-900">{selectedItem.location}</p>
                        </div>
                        <div className="col-span-2">
                          <label className="text-sm font-medium text-gray-600">Description</label>
                          <p className="text-base text-gray-900">{selectedItem.description || 'No description provided'}</p>
                        </div>
                      </div>
                      
                      {/* Police Report Photos */}
                      {selectedItem.police_report_photos && selectedItem.police_report_photos.length > 0 && (
                        <div className="pt-4 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3">Police Report Documents</h4>
                          <div className="grid grid-cols-3 gap-3">
                            {selectedItem.police_report_photos.map((photo, index) => {
                              const imageUrl = photo.startsWith('http') ? photo : `${API}${photo}`;
                              return (
                                <a 
                                  key={index} 
                                  href={imageUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img 
                                    src={imageUrl} 
                                    alt={`Police report ${index + 1}`}
                                    className="w-full h-32 object-cover rounded border hover:opacity-75 transition"
                                  />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Bike Damage Photos */}
                      {selectedItem.bike_photos && selectedItem.bike_photos.length > 0 && (
                        <div className="pt-4 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3">Bike Damage Photos</h4>
                          <div className="grid grid-cols-3 gap-3">
                            {selectedItem.bike_photos.map((photo, index) => {
                              const imageUrl = photo.startsWith('http') ? photo : `${API}${photo}`;
                              return (
                                <a 
                                  key={index} 
                                  href={imageUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img 
                                    src={imageUrl} 
                                    alt={`Bike damage photo ${index + 1}`}
                                    className="w-full h-32 object-cover rounded border hover:opacity-75 transition"
                                  />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Submission Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created By</label>
                            <p className="text-base text-gray-900">{selectedItem.created_by}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedItem.type === 'rider' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Name</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Email</label>
                          <p className="text-base text-gray-900">{selectedItem.email || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Phone Number</label>
                          <p className="text-base text-gray-900">{selectedItem.mobile_uae || selectedItem.phone_number || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Nationality</label>
                          <p className="text-base text-gray-900">{selectedItem.nationality || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Emirates ID</label>
                          <p className="text-base text-gray-900">{selectedItem.emirates_id || selectedItem.emirates_id_number || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">License Number</label>
                          <p className="text-base text-gray-900">{selectedItem.license_number || 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">License Expiry</label>
                          <p className="text-base text-gray-900">{selectedItem.license_expiry || selectedItem.license_expiry_date ? formatDateDisplay(selectedItem.license_expiry || selectedItem.license_expiry_date) : 'N/A'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Assigned Bike</label>
                          <p className="text-base text-gray-900">{selectedItem.assigned_bike_plate || 'Not assigned'}</p>
                        </div>
                      </div>
                      
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Submission Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created By</label>
                            <p className="text-base text-gray-900">{selectedItem.created_by || 'Bulk Upload'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created At</label>
                            <p className="text-base text-gray-900">{selectedItem.created_at ? formatDateDisplay(selectedItem.created_at) : 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedItem.type === 'lessee' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Company Name</label>
                          <p className="text-base font-semibold text-gray-900">{selectedItem.company_name || selectedItem.name}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">TRN Number</label>
                          <p className="text-base text-gray-900">{selectedItem.trn_no || selectedItem.trn_number || 'N/A'}</p>
                        </div>
                        <div className="col-span-2">
                          <label className="text-sm font-medium text-gray-600">Address</label>
                          <p className="text-base text-gray-900">{selectedItem.address || 'N/A'}</p>
                        </div>
                      </div>
                      
                      {/* Owner Details Section */}
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Owner Details</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Owner Name</label>
                            <p className="text-base text-gray-900">{selectedItem.owner_name || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Emirates ID</label>
                            <p className="text-base text-gray-900">{selectedItem.owner_emirates_id || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Passport No.</label>
                            <p className="text-base text-gray-900">{selectedItem.owner_passport_no || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Contact Person Section */}
                      <div className="pt-4 border-t bg-green-50 -mx-4 px-4 py-3 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Contact Person (Primary Communication)
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Name</label>
                            <p className="text-base text-gray-900">{selectedItem.contact_person_name || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Designation</label>
                            <p className="text-base text-gray-900">{selectedItem.contact_designation || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Email</label>
                            <p className="text-base text-gray-900">{selectedItem.contact_email || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Mobile</label>
                            <p className="text-base text-gray-900">{selectedItem.contact_mobile || 'N/A'}</p>
                          </div>
                        </div>
                        <p className="text-xs text-green-700 mt-3">
                          📧 All system notifications and critical alerts will be sent to this contact
                        </p>
                      </div>
                      
                      {/* Trade Licenses */}
                      {selectedItem.trade_licenses && selectedItem.trade_licenses.length > 0 && (
                        <div className="pt-4 border-t">
                          <h4 className="font-semibold text-gray-900 mb-3">Trade Licenses</h4>
                          <div className="grid grid-cols-2 gap-3">
                            {selectedItem.trade_licenses.map((license, index) => (
                              <div key={index} className="bg-slate-50 p-3 rounded border">
                                <p className="font-medium text-sm">{license.city}</p>
                                <p className="text-sm text-gray-600">License: {license.license_no}</p>
                                <p className="text-sm text-gray-600">Expiry: {license.expiry}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-4 border-t">
                        <h4 className="font-semibold text-gray-900 mb-3">Submission Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created By</label>
                            <p className="text-base text-gray-900">{selectedItem.created_by}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">Created At</label>
                            <p className="text-base text-gray-900">{selectedItem.created_at ? formatDateDisplay(selectedItem.created_at) : 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Rejection Reason (only show if rejecting) */}
              <div className="space-y-2">
                <Label>Rejection Reason (Optional for Approve, Required for Reject)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {processing ? (
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Approve
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={processing}
                  variant="destructive"
                  className="flex-1"
                >
                  {processing ? (
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Approvals;
